import { Card, Kpi, StatusChip, PlanBadge, naira, orgMrr, needsAttention, orgAccess } from '../ui'
import { timeAgo } from '../../utils/timeAgo'

const AUDIT_LABELS = {
  org_create: 'Created organization',
  org_update: 'Updated organization',
  org_delete: 'Deleted organization',
  plan_trial_update: 'Updated plan / trial',
  user_create: 'Created user',
  user_assign: 'Assigned user to branch',
  user_deactivate: 'Deactivated user',
  user_reactivate: 'Reactivated user',
  user_password_reset: 'Reset password',
  user_delete: 'Deleted user',
  broadcast_send: 'Sent email broadcast',
  plan_catalogue_update: 'Edited plan catalogue',
}

export default function Overview({ organizations, branches, users, auditEntries = [], onManage, onGoto }) {
  const byPlan = { starter: 0, growth: 0, enterprise: 0 }
  organizations.forEach((o) => { byPlan[o.plan || 'starter'] = (byPlan[o.plan || 'starter'] || 0) + 1 })
  const mrr = organizations.reduce((s, o) => s + orgMrr(o), 0)
  const activeUsers = users.filter((u) => u.isActive !== false).length
  const attention = organizations.filter(needsAttention)
    // expired/past-due first, then soonest-ending
    .sort((a, b) => {
      const rank = { past_due: 0, expired: 0, cancelled: 1, ending: 2 }
      return (rank[orgAccess(a).key] ?? 3) - (rank[orgAccess(b).key] ?? 3)
    })
  const newest = [...organizations]
    .filter((o) => o.createdAt)
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 4)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Organizations" value={organizations.length}
          sub={`${byPlan.enterprise} enterprise · ${byPlan.growth} growth · ${byPlan.starter} starter`} />
        <Kpi label="Active users" value={activeUsers}
          sub={`${users.length - activeUsers} deactivated`} />
        <Kpi label="MRR" value={naira(mrr)}
          sub={`${organizations.filter((o) => o.subscriptionStatus === 'active').length} paying`} />
        <Kpi label="Needs attention" value={attention.length}
          valueClass={attention.length > 0 ? 'text-red-600' : 'text-gray-800'}
          sub="expired, ending or past due" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Trials needing attention" hint="lock-outs cost renewals">
          {attention.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">All organizations are in good standing. 🎉</p>
          )}
          <ul>
            {attention.map((org) => (
              <li key={org.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{org.name}</p>
                  <p className="text-xs text-gray-400 capitalize">
                    {org.plan || 'starter'} · {branches.filter((b) => b.organizationId === org.id).length} branch(es)
                  </p>
                </div>
                <StatusChip org={org} />
                <button onClick={() => onManage(org)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
                  Extend
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Newest organizations">
          {newest.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">No organizations yet.</p>
          )}
          <ul>
            {newest.map((org) => (
              <li key={org.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{org.name}</p>
                  <p className="text-xs text-gray-400">
                    joined {org.createdAt.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <PlanBadge plan={org.plan} />
                <StatusChip org={org} />
              </li>
            ))}
          </ul>
          <button onClick={() => onGoto('orgs')}
            className="w-full text-center text-xs font-semibold text-teal-700 hover:bg-teal-50 py-2.5 border-t border-gray-100 rounded-b-xl transition">
            View all organizations →
          </button>
        </Card>
      </div>

      <Card title="Recent activity" hint="superadmin actions — audit log">
        {auditEntries.length === 0 ? (
          <p className="px-4 py-5 text-sm text-gray-400 text-center">
            Actions you take in HQ (plan changes, user edits, broadcasts) appear here.
            Requires comped_audit_migration.sql.
          </p>
        ) : (
          <ul className="max-h-72 overflow-y-auto">
            {auditEntries.map((e) => (
              <li key={e.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">
                    <span className="font-semibold">{AUDIT_LABELS[e.action] || e.action}</span>
                    {e.target && <span className="text-gray-500"> — {e.target}</span>}
                  </p>
                </div>
                <span className="text-[11px] text-gray-400 flex-none">{e.actorName?.split(' ')[0]} · {timeAgo(e.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
